import { LogEvents } from "../../../utils/LogEvents";
import { SSO_LOGIN_URL } from "../../../AppConstants";
import { RetryServiceCaller, RetryServiceCallUntilSuccessOrTimeout } from "../../utils/Utils";
import { ErrorStringFromException } from "../../../utils/utils";
import { LogEvent, LogSingleEvent } from "../../TelemetryLogger/LogEvent";
import { UIAuthProvider } from "../../interfaces/IAuthenticationProvider";
import { ILogData, LogEventNames } from "../../interfaces/ITelemetryLogger";
import { HostAppSettings, HostAppSettingName } from "../HostAppSettings";

enum GetAccessTokenDiagKeys {
    Succeeded = 'Succeeded',
    FailureReason = 'FailureReason',
    UpnMismatchType1 = 'UpnMismatchType1',
    UpnMismatchType2 = 'UpnMismatchType2',
    UpnMismatchDiagFailReason = 'UpnMismatchDiagFailReason'
}

// This class contains shared implementations for PpeAuthenticationProvider and ProdAuthenticationProvider
export class AuthSharedComponent {
    private officeFirstPartyAuthLoaded: boolean = false;
    private authenticationTask: Promise<void> | null = null;
    private uiAuthProvider: UIAuthProvider | null = null;

    Authenticate(): void {
        LogEvents.ServiceAuthentication.Start();

        (<any>OfficeFirstPartyAuth).authFlow = "authcode";

        const ShouldSkipUpnCheck: boolean = HostAppSettings.IsSettingTrue(HostAppSettingName.ShouldSkipUpnCheck);
        const ShouldEnableFlightAuthVersion: boolean = HostAppSettings.IsSettingTrue(HostAppSettingName.EnableFlightAuthVersion);

        if (ShouldSkipUpnCheck) {
            (<any>OfficeFirstPartyAuth).upnCheck = false;
        }

        if (ShouldEnableFlightAuthVersion) {
            (<any>OfficeFirstPartyAuth).authVersion = 'flight';
        }

        if (this.officeFirstPartyAuthLoaded) {
            LogEvents.ServiceAuthentication.AddResponse({ isSuccessful: true, message: "Success", isFinalResponse: true });
            return;
        }

        this.authenticationTask = RetryServiceCallUntilSuccessOrTimeout(
            LogEvents.ServiceAuthentication /*logServiceEvent*/,
            () => OfficeFirstPartyAuth.load(SSO_LOGIN_URL) /*throwingServiceCall*/,
            false /*shouldRetryOnNull*/,
            RetryServiceCaller.OfficeFirstPartyAuthLoad
        ).then(() => {
            this.officeFirstPartyAuthLoaded = true;
        });
    }

    async GetAccessToken(resource: string): Promise<string> {
        LogEvents.ServiceGetAccessToken.Start();
        if (!this.officeFirstPartyAuthLoaded) {
            if (this.authenticationTask == null) {
                const message: string = "GetAccessToken: Call Authenticate before calling GetAccessToken!";
                LogEvents.ServiceGetAccessToken.AddResponse({ isSuccessful: false, message, isFinalResponse: true});
                throw new Error(message);
            } else {
                try {
                    await this.authenticationTask;
                } catch (error) {
                    LogEvents.ServiceGetAccessToken.AddResponse({ isSuccessful: false, message: "GetAccessToken: Authenticate failed with error: " + ErrorStringFromException(error), isFinalResponse: true});
                    throw error;
                }
            }
        }

        let popupRequired: boolean = false;
        let popupWithAppUIRequired: boolean = false;
        let token: OfficeFirstPartyAuth.AuthResult = await RetryServiceCallUntilSuccessOrTimeout(
            LogEvents.ServiceGetAccessToken /*logServiceEvent*/,
            () => {
                try {
                    let tokenPromise:Promise<OfficeFirstPartyAuth.AuthResult> | null = null;
                    if (popupWithAppUIRequired) {
                        if (this.uiAuthProvider == null) {
                            throw new Error('No UI auth provider was registered. Unable to show an AuthPopup');
                        }

                        tokenPromise = this.uiAuthProvider(() => this.CallOFPAGetAccessToken(resource, true /*popupRequired*/));
                    } else {
                        tokenPromise = this.CallOFPAGetAccessToken(resource, popupRequired /*popupRequired*/)
                    }

                    popupRequired = true;
                    return tokenPromise;
                } catch (e) {
                    return Promise.reject(e);
                }
            } /*throwingServiceCall*/,
            true /*shouldRetryOnNull*/,
            RetryServiceCaller.CallOFPAGetAccessToken,
            (e) => {
                if (e && e.code == 'upn_mismatch') {
                    this.LogDiagnosticTelemForUpnMismatches();
                }

                if (popupWithAppUIRequired) {
                    return false;
                } else if (e && e.code == 'popup_window_error') {
                    popupWithAppUIRequired = true;
                }

                return true;
            } /*shouldRetryError*/,
            () => popupRequired /*shouldIgnoreTimeout*/
        );

        return token.accessToken;
    }

    SetUIAuthProvider(uiAuthProvider: UIAuthProvider): void {
        this.uiAuthProvider = uiAuthProvider;
    }

    private CallOFPAGetAccessToken(resource: string, popupRequired: boolean): Promise<any> {
        const GATlogEvent = new LogEvent<GetAccessTokenDiagKeys>(LogEventNames.GetAccessTokenDiag);
        try {
            GATlogEvent.Start();
            const tokenPromise:Promise<OfficeFirstPartyAuth.AuthResult> = OfficeFirstPartyAuth.getAccessToken({ resource }, popupRequired ? { popup: true } : undefined) /*throwingServiceCall*/
            GATlogEvent.End([{key: GetAccessTokenDiagKeys.Succeeded, value: true}]);
            return tokenPromise;
        } catch(e) {
            GATlogEvent.End([{key: GetAccessTokenDiagKeys.Succeeded, value: false}, {key: GetAccessTokenDiagKeys.FailureReason, value: ErrorStringFromException(e)} ]);
            throw e;
        }
    }

    private LogDiagnosticTelemForUpnMismatches(): void {
        OfficeFirstPartyAuth.getPrimaryIdentityInfo().then(
            (primaryIdentityInfo: OfficeCore.OfficeIdentityInfo) => {
                let data: ILogData<GetAccessTokenDiagKeys>[] = [];
                if (this.isStringAPhoneNumber(primaryIdentityInfo.email.split('@')[0])) {
                    data.push({ key: GetAccessTokenDiagKeys.UpnMismatchType1, value: true });
                } else if (this.isStringAPhoneNumber(primaryIdentityInfo.uniqueId)) {
                    data.push({ key: GetAccessTokenDiagKeys.UpnMismatchType2, value: true });
                }

                if (data.length != 0) {
                    LogSingleEvent<GetAccessTokenDiagKeys>(LogEventNames.GetAccessTokenDiag, data);
                }
            } /*onFulfilled*/,
            (reason: any) => {
                LogSingleEvent<GetAccessTokenDiagKeys>(LogEventNames.GetAccessTokenDiag,
                    [{ key: GetAccessTokenDiagKeys.UpnMismatchDiagFailReason, value: ErrorStringFromException(reason) }]);
            } /*onRejected*/
        );
    }

    private isStringAPhoneNumber(input: string): boolean {
        // Regex from https://stackoverflow.com/questions/4338267/validate-phone-number-with-javascript
        return (input.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im) != null);
    }
}
